import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useContractorAgreementReSigning } from "@src/appV2/Agreements/hooks/useContractorAgreementReSigning";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PdfViewerProvider } from "@src/appV2/PdfViewer/context";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { type Worker } from "@src/appV2/Worker/api/types";

import { PageWrapper } from "../components/PageWrapper";
import { StickyScrollPageHeader } from "../components/StickyScrollPageHeader";
import { useGetICAgreementToSign } from "./hooks/useGetICAgreementToSign";
import { ICAgreementFormV2 } from "./ICAgreementFormV2";

interface OnboardingToStripeICAgreementContainerProps {
  worker: Worker;
  onSignAgreement: (agreementVersion: string) => Promise<void>;
  isSigningAgreement: boolean;
}

export function OnboardingToStripeICAgreementContainer(
  props: OnboardingToStripeICAgreementContainerProps
) {
  const { worker, onSignAgreement, isSigningAgreement } = props;
  const { agreementToSign, pdfUrl, agreementsStatus } = useGetICAgreementToSign({
    workerId: worker.userId,
  });

  const { needsReSigning } = useContractorAgreementReSigning({ worker });

  useLogEffect(
    APP_V2_USER_EVENTS.SIGN_AGREEMENT_VIEWED,
    {
      hcpId: worker.userId,
      name: worker.name,
      agreementVersion: agreementToSign?.attributes.version,
      isReSigning: needsReSigning,
    },
    { enabled: isDefined(agreementToSign) }
  );

  useLogEffect(
    APP_V2_APP_EVENTS.CONTRACTOR_AGREEMENT_RE_SIGNING_VIEWED,
    {
      hcpId: worker.userId,
      name: worker.name,
      agreementVersion: agreementToSign?.attributes.version,
    },
    { enabled: needsReSigning && isDefined(agreementToSign) }
  );

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          hideBackButton
          title="Independent Contractor Agreement"
          headerVariant="tertiary"
        />
        <PdfViewerProvider fileName="clipboard_health_contract.pdf" pdfUrl={pdfUrl}>
          <ICAgreementFormV2
            worker={worker}
            agreementToSign={agreementToSign}
            agreementsStatus={agreementsStatus}
            isSigningAgreement={isSigningAgreement}
            onSignAgreement={async ({ agreementToSign }) => {
              await onSignAgreement(`V${agreementToSign.attributes.version}`);
            }}
          />
        </PdfViewerProvider>
      </PageWrapper>
    </ThemeProvider>
  );
}
